import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LANGUAGES } from '../i18n/translations';

const STORAGE_KEY = 'language';
const DEFAULT_LANGUAGE = 'en';

const LanguageContext = createContext(null);

/** Fill {placeholders} from a values object. Unknown ones are left alone. */
function interpolate(template, values) {
  if (!values) return template;
  return template.replace(/\{(\w+)\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match
  );
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return LANGUAGES[saved] ? saved : DEFAULT_LANGUAGE;
    } catch {
      return DEFAULT_LANGUAGE;
    }
  });

  // The choice has to survive a reload - a Nepali-speaking cashier should never
  // have to set it again at the start of a shift.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch {
      // Private mode / storage full - the app still works, the choice just
      // does not persist.
    }
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((code) => {
    if (LANGUAGES[code]) setLanguageState(code);
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguageState((current) => (current === 'ne' ? 'en' : 'ne'));
  }, []);

  /**
   * Translate. The English text is the key, so an untranslated string renders
   * as itself rather than disappearing.
   *
   *   t('Orders')                     -> 'अर्डरहरू'
   *   t('Token #{n} started.', {n:5}) -> 'टोकन #5 सुरु भयो।'
   */
  const t = useCallback(
    (text, values) => {
      if (text == null) return text;
      const dict = LANGUAGES[language]?.dict || {};
      return interpolate(dict[text] ?? text, values);
    },
    [language]
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      toggleLanguage,
      t,
      languages: LANGUAGES,
      isNepali: language === 'ne',
    }),
    [language, setLanguage, toggleLanguage, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;
